import * as logClient from "@classdojo/log-client";
import { ref, StoreWithProps, useClassState } from "@web-monorepo/hooks";
import { shuffle } from "es-toolkit";
import { useEffect } from "react";
import type { Person } from "components/PersonListItem";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { generateLoadingAnimation, randomIndex } from "utils";

export type UseWhoseTurnCardContentProps = {
  persons: Person[];
};

export type UseWhoseTurnCardContentResult = {
  shuffling: boolean;
  loadingAvatar: string | undefined;
  currentPerson: Person | undefined;
  previousPerson: Person | undefined;
  nextPerson: Person | undefined;
  currentIndex: number;
  showFirstShuffledPerson: boolean;
  reshuffle: () => void;
  moveToPreviousPerson: () => void;
  moveToNextPerson: () => void;
};

const BLINK_INTERVAL_MS = 200;
const BLINK_DURATION_MS = 800;
const SHUFFLE_DURATION_MS = 3000;

/**
 * Valtio-backed state machine for the "Whose Turn" picker:
 *   1. Reshuffle the person list and run a 6-step loading animation
 *      (handled by the existing `generateLoadingAnimation` util).
 *   2. After the animation settles, play the "finish" chime and start a
 *      200ms blink on the chosen person's nameplate.
 *   3. After 800ms of blinking, stop and leave the name solid.
 *
 * Audio elements are queried by DOM id (`#rolling-audio` / `#finish-audio`)
 * because they live in the WhoseTurn parent — the global lookup is preserved
 * verbatim from the pre-migration component. Replace with refs in a future
 * pass if/when the audio architecture is consolidated.
 */
class WhoseTurnCardContentStore extends StoreWithProps<UseWhoseTurnCardContentProps> {
  shuffledPersons: Person[] = [];
  currentIndex = 0;
  shuffling = true;
  loadingAvatar: string | undefined = undefined;
  showFirstShuffledPerson = true;

  /** Non-reactive container for the blink interval handle. */
  private blink = ref({ handle: null as ReturnType<typeof window.setInterval> | null });
  /** Non-reactive timeout handles for the delayed blink start + stop. */
  private blinkTimers = ref({
    start: null as ReturnType<typeof window.setTimeout> | null,
    stop: null as ReturnType<typeof window.setTimeout> | null,
  });

  get currentPerson(): Person | undefined {
    if (this.shuffledPersons.length === 0) return undefined;
    return this.shuffledPersons[this.currentIndex % this.shuffledPersons.length];
  }

  get previousPerson(): Person | undefined {
    if (this.shuffledPersons.length === 0) return undefined;
    return this.shuffledPersons[(this.currentIndex - 1) % this.shuffledPersons.length];
  }

  get nextPerson(): Person | undefined {
    if (this.shuffledPersons.length === 0) return undefined;
    return this.shuffledPersons[(this.currentIndex + 1) % this.shuffledPersons.length];
  }

  reshuffle() {
    this.playAudio("#rolling-audio");
    this.shufflePeople();
    this.scheduleBlink();
  }

  moveToPreviousPerson() {
    logParentToolkitEvent({ eventName: `whose_turn.previous.tap` });
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
  }

  moveToNextPerson() {
    logParentToolkitEvent({ eventName: `whose_turn.next.tap` });
    this.currentIndex += 1;
  }

  stop() {
    this.clearBlinkTimers();
  }

  private shufflePeople() {
    const { persons } = this.props;
    if (persons.length === 0) return;
    this.shuffledPersons = shuffle(persons);
    this.currentIndex = 0;
    let currentAvatar = this.loadingAvatar;
    generateLoadingAnimation({
      numSegments: 6,
      endIntervalDuration: 100,
      startIntervalDuration: 300,
      onAnimationStateChange: (isShuffling: boolean) => {
        this.shuffling = isShuffling;
      },
      onAnimationTick: () => {
        const filtered = persons.filter((p) => p.avatar !== currentAvatar);
        currentAvatar = filtered[randomIndex(filtered)]?.avatar ?? currentAvatar;
        this.loadingAvatar = currentAvatar;
      },
    })();
  }

  private scheduleBlink() {
    this.clearBlinkTimers();
    this.blinkTimers.start = window.setTimeout(() => {
      this.playAudio("#finish-audio");
      this.blink.handle = window.setInterval(() => {
        this.showFirstShuffledPerson = !this.showFirstShuffledPerson;
      }, BLINK_INTERVAL_MS);
      this.blinkTimers.stop = window.setTimeout(() => {
        this.showFirstShuffledPerson = true;
        if (this.blink.handle !== null) {
          window.clearInterval(this.blink.handle);
          this.blink.handle = null;
        }
      }, BLINK_DURATION_MS);
    }, SHUFFLE_DURATION_MS);
  }

  private clearBlinkTimers() {
    if (this.blinkTimers.start !== null) {
      window.clearTimeout(this.blinkTimers.start);
      this.blinkTimers.start = null;
    }
    if (this.blinkTimers.stop !== null) {
      window.clearTimeout(this.blinkTimers.stop);
      this.blinkTimers.stop = null;
    }
    if (this.blink.handle !== null) {
      window.clearInterval(this.blink.handle);
      this.blink.handle = null;
    }
  }

  /**
   * TODO: lift the audio elements into refs (passed via props or context) so
   * the VM doesn't depend on global DOM ids. The current pattern is preserved
   * verbatim from the pre-migration component; replacing it requires changing
   * the parent (WhoseTurn) audio architecture, out of scope for the
   * sx→CSS Modules migration. Don't propagate this `document.querySelector`
   * pattern to new code.
   */
  private playAudio(selector: string) {
    const node = document.querySelector<HTMLAudioElement>(selector);
    node?.play().catch(() => {
      logClient.sendMetrics([{ type: "increment", value: 1, metricName: "toolkit.media.audio.play.error" }]);
    });
  }
}

export function useWhoseTurnCardContent(props: UseWhoseTurnCardContentProps): UseWhoseTurnCardContentResult {
  const store = useClassState(WhoseTurnCardContentStore, props);
  // `useClassState` returns a fresh Proxy wrapper each render (NOT stable),
  // but the bound methods inside it (`reshuffle`/`stop`) are stable
  // references on the underlying Valtio proxy. Destructure them so the effect
  // deps array can be satisfied without triggering an infinite re-render
  // loop on every store mutation.
  const { reshuffle, stop } = store;

  // Re-shuffle whenever the `persons` list identity changes — the consumer
  // passes a fresh array when add/toggle person actions happen. Cleanup on
  // unmount clears all pending blink timers so unmount mid-shuffle doesn't
  // mutate state on a dead store.
  useEffect(() => {
    reshuffle();
    return () => stop();
  }, [props.persons, reshuffle, stop]);

  return {
    shuffling: store.shuffling,
    loadingAvatar: store.loadingAvatar,
    currentPerson: store.currentPerson,
    previousPerson: store.previousPerson,
    nextPerson: store.nextPerson,
    currentIndex: store.currentIndex,
    showFirstShuffledPerson: store.showFirstShuffledPerson,
    reshuffle: store.reshuffle,
    moveToPreviousPerson: store.moveToPreviousPerson,
    moveToNextPerson: store.moveToNextPerson,
  };
}
